import axios from '@/src/api/axios_init'
import { filterDstDuplicates } from '@/src/utils/filterDstDuplicates'
import { mean } from 'mathjs'
import type { MeterResponseDto } from '../api/types/generated-types'

export const getLocation = async locationId => {
  const response = await axios.get('/firebase/location', {
    params: {
      companyId: locationId,
    },
  })

  const { data } = response
  return data
}

export const calculateCorrelationCoefficient = (dataA, dataB, valueFieldA, valueFieldB) => {
  let corrCoeffQuarterlyData: null | number = null
  const entriesA = dataA
  const entriesB = dataB

  let samplesA: any = []
  let samplesB: any = []
  let sampleSize = 0

  samplesA = entriesA.map(i => {
    if (i[valueFieldA] === null || i[valueFieldA] === undefined) {
      return 0
    }
    return i[valueFieldA]
  })

  samplesB = entriesB.map(i => {
    if (i[valueFieldB] === null || i[valueFieldB] === undefined) {
      return 0
    }
    return i[valueFieldB]
  })

  sampleSize = Math.min(samplesA.length, samplesB.length)

  let numerator = 0
  let denominatorA = 0
  let denominatorB = 0
  let denominator = 1

  if (!samplesA.length && !samplesB.length) {
    return null
  }
  const meanA = mean(samplesA)
  const meanB = mean(samplesB)
  for (let i = 0; i < sampleSize; i++) {
    const a = samplesA[i] - meanA
    const b = samplesB[i] - meanB
    const c = a * b
    numerator += c

    denominatorA += Math.pow(a, 2)
    denominatorB += Math.pow(b, 2)
  }
  denominatorA = Math.sqrt(denominatorA)
  denominatorB = Math.sqrt(denominatorB)
  denominator = denominatorA * denominatorB
  corrCoeffQuarterlyData = numerator / denominator
  if (Number.isNaN(corrCoeffQuarterlyData)) corrCoeffQuarterlyData = 0

  return corrCoeffQuarterlyData
}

export const transformMeterDataForVisualization = (
  meter: MeterResponseDto,
  comparisonMeter?: MeterResponseDto,
  granularity: 'original' | 'daily' = 'original'
) => {
  const allMeters = [meter, comparisonMeter].filter(Boolean)

  if (!allMeters.length) {
    return {
      quarterlyDataKW: [],
      corrCoeffQuarterlyData: null,
      dailyDataKWh: [],
      totalConsumptionYtd: null,
      totalConsumptionInSelectedTimeframe: null,
      peakloadYtd: null,
    }
  }

  const quarterlyData: any[] = []
  let totalConsumptionInSelectedTimeframe = 0
  let totalConsumptionYtd = 0
  let peakloadYtd = 0
  // Tracks whether any meter reported a real (non-null) value, so an all-missing total can be
  // told apart from a total that is genuinely 0.
  let hasConsumptionInSelectedTimeframe = false
  let hasConsumptionYtd = false
  let hasPeakloadYtd = false

  // Process each meter
  for (let i = 0; i < allMeters.length; i++) {
    const meter = allMeters[i]
    if (!meter) continue

    const meterName = meter.meterTitle || `Meter ${meter.deviceId}`

    // Process data series (same for both quarterly and daily - depends on granularity param)
    const consumptionSeries = meter.consumptionData?.dataSeries || []
    for (let j = 0; j < consumptionSeries.length; j++) {
      const entry = consumptionSeries[j]

      // Use discriminated union to get the correct value based on energy type
      let value: number | null = null
      if (entry.type === 'kwh') {
        if (granularity === 'daily') value = entry.value_kwh ?? null
        else value = entry.value_kw ?? null
      } else {
        if (granularity === 'daily') value = entry.value_m3 ?? null
        else value = entry.value_m3h ?? null
      }

      if (quarterlyData[j]) {
        quarterlyData[j][meterName] = value
      } else {
        quarterlyData[j] = { [meterName]: value }
      }

      quarterlyData[j].timestamp = entry.timestamp
    }

    // Add to daily consumption total
    if (meter.consumptionData?.consumptionInTimeframe != null) {
      totalConsumptionInSelectedTimeframe += meter.consumptionData.consumptionInTimeframe
      hasConsumptionInSelectedTimeframe = true
    }
    if (meter.consumptionData?.consumptionYtd != null) {
      totalConsumptionYtd += meter.consumptionData.consumptionYtd
      hasConsumptionYtd = true
    }
    if (meter.consumptionData?.peakloadYtd != null) {
      peakloadYtd += meter.consumptionData.peakloadYtd
      hasPeakloadYtd = true
    }
  }

  // Calculate correlation coefficient if comparing two meters
  let corrCoeffQuarterlyData: number | null = null
  if (allMeters.length === 2) {
    const meter1Series = allMeters[0]?.consumptionData?.dataSeries || []
    const meter2Series = allMeters[1]?.consumptionData?.dataSeries || []

    if (meter1Series.length && meter2Series.length) {
      // Determine field names based on meter type and granularity
      const meter1Type = meter1Series[0]?.type
      const meter2Type = meter2Series[0]?.type

      const getFieldName = (type: string) => {
        if (type === 'kwh') {
          return granularity === 'daily' ? 'value_kwh' : 'value_kw'
        } else {
          return granularity === 'daily' ? 'value_m3' : 'value_m3h'
        }
      }

      corrCoeffQuarterlyData = calculateCorrelationCoefficient(
        meter1Series,
        meter2Series,
        getFieldName(meter1Type),
        getFieldName(meter2Type)
      ) as number | null
    }
  }

  // Filter out DST duplicates with nulls
  const filteredQuarterlyData = filterDstDuplicates(quarterlyData, { valueKey: null })

  // Create daily data with "day" instead of "timestamp"
  const dailyData = filteredQuarterlyData.map(entry => {
    const { timestamp, ...rest } = entry
    return {
      ...rest,
      day: timestamp, // Convert timestamp to day for daily data
    }
  })

  return {
    quarterlyDataKW: filteredQuarterlyData,
    corrCoeffQuarterlyData,
    dailyDataKWh: dailyData,
    totalConsumptionYtd: hasConsumptionYtd ? totalConsumptionYtd : null,
    totalConsumptionInSelectedTimeframe: hasConsumptionInSelectedTimeframe ? totalConsumptionInSelectedTimeframe : null,
    peakloadYtd: hasPeakloadYtd ? peakloadYtd : null,
  }
}
